// ══════════════════════════════════════════════════════════
// CHECKOUT SERVICE
//
// Flujo de checkout hacia el backend PHP.
//
// Toda comunicación con el backend PHP pasa por rfwBackend.
// NUNCA se envía price, currency, payment_status ni
// subscription_status desde el frontend.
//
// FRONTEND NO PUEDE:
// - procesar pagos directamente
// - marcar pagos como completados
// - activar suscripciones anticipadamente
// - modificar payment_status
// - modificar subscription_status
// - cambiar plan_id de una suscripción
// ══════════════════════════════════════════════════════════

import { backendApiRequest, RfwBackendError } from '@/services/rfwBackend';
import type {
  CheckoutRequest,
  CheckoutResponse,
  CheckoutStatusResponse,
  BackendPlan,
  BackendSubscription,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  StreamingMode,
  PlanCompatibilityResult,
  CheckoutError,
  BillingPeriod,
} from '@/types/checkout';
import {
  checkPlanCompatibility,
  mapCheckoutError,
  buildCheckoutUrls,
} from '@/types/checkout';

// ═══════════════ PLANS ═══════════════

/**
 * Obtener planes desde el backend PHP.
 * GET /api/v1/plans
 *
 * Precio y moneda provienen del backend.
 * El frontend NUNCA asume ni calcula precios.
 */
export async function getBackendPlans(): Promise<BackendPlan[]> {
  const response = await backendApiRequest<BackendPlan[]>('/api/v1/plans');
  return response.data;
}

/**
 * Obtener un plan específico desde el backend.
 */
export async function getBackendPlanById(planId: string): Promise<BackendPlan | null> {
  const plans = await getBackendPlans();
  return plans.find((p) => p.id === planId) ?? null;
}

/**
 * Filtrar planes compatibles con una modalidad de streaming.
 */
export async function getCompatiblePlans(
  streamingMode: StreamingMode,
): Promise<{ plan: BackendPlan; compatibility: PlanCompatibilityResult }[]> {
  const plans = await getBackendPlans();
  return plans.map((plan) => ({
    plan,
    compatibility: checkPlanCompatibility(plan, streamingMode),
  }));
}

// ═══════════════ SUBSCRIPTION ═══════════════

/**
 * Obtener la suscripción activa del usuario autenticado desde el backend PHP.
 * GET /api/v1/me/subscription
 *
 * Solo lectura. El frontend NUNCA modifica el estado de la suscripción.
 */
export async function getBackendMySubscription(): Promise<BackendSubscription | null> {
  try {
    const response = await backendApiRequest<BackendSubscription | null>('/api/v1/me/subscription');
    return response.data ?? null;
  } catch (err: unknown) {
    // 404 = sin suscripción activa → es un estado válido
    if (err instanceof RfwBackendError && err.statusCode === 404) {
      return null;
    }
    throw err;
  }
}

// ═══════════════ CHECKOUT ═══════════════

/**
 * Crear un checkout en el backend PHP.
 * POST /api/v1/checkouts
 *
 * El frontend SOLO envía:
 * - plan_id
 * - radio_id (opcional)
 * - billing_period
 * - success_url
 * - cancel_url
 *
 * PROHIBIDO enviar price, currency, payment_status,
 * subscription_status, provider_reference, organization_id,
 * profile_id, discount.
 *
 * El backend devuelve checkout_url a la que se redirige al usuario.
 */
export async function createCheckout(
  planId: string,
  billingPeriod: BillingPeriod,
  streamingMode: StreamingMode,
  radioId?: string,
): Promise<CheckoutResponse> {
  // ══════════ VALIDACIÓN DE COMPATIBILIDAD ══════════
  // Antes de enviar al backend, verificamos compatibilidad localmente
  // para dar feedback inmediato al usuario.

  const plan = await getBackendPlanById(planId);
  if (!plan) {
    throw mapCheckoutError(404);
  }

  const compatibility = checkPlanCompatibility(plan, streamingMode);
  if (!compatibility.compatible) {
    throw {
      ...mapCheckoutError(422, compatibility.reason),
      status_code: 422,
    };
  }

  // ══════════ CONSTRUIR PAYLOAD ══════════
  // Solo los campos autorizados. NADA de price, currency, etc.

  const urls = buildCheckoutUrls(
    (typeof window !== 'undefined'
      ? (window as unknown as Record<string, unknown>).__BASE_PATH__ as string ?? ''
      : ''),
  );

  const payload: CheckoutRequest = {
    plan_id: planId,
    billing_period: billingPeriod,
    success_url: urls.success_url,
    cancel_url: urls.cancel_url,
  };

  if (radioId) {
    payload.radio_id = radioId;
  }

  // ══════════ ENVIAR AL BACKEND ══════════
  try {
    const response = await backendApiRequest<CheckoutResponse>('/api/v1/checkouts', {
      method: 'POST',
      body: payload as unknown as Record<string, unknown>,
    });

    return response.data;
  } catch (err: unknown) {
    if (err instanceof RfwBackendError) {
      throw mapCheckoutError(err.statusCode, err.message);
    }
    throw err;
  }
}

/**
 * Consultar el estado de un checkout por ID.
 * GET /api/v1/checkouts/{checkoutId}
 *
 * Solo lectura. El frontend consulta, no modifica.
 */
export async function getCheckoutStatus(checkoutId: string): Promise<CheckoutStatusResponse> {
  try {
    const response = await backendApiRequest<CheckoutStatusResponse>(
      `/api/v1/checkouts/${encodeURIComponent(checkoutId)}`,
    );
    return response.data;
  } catch (err: unknown) {
    if (err instanceof RfwBackendError) {
      throw mapCheckoutError(err.statusCode, err.message);
    }
    throw err;
  }
}

/**
 * Determinar si un estado de checkout es terminal.
 */
export function isCheckoutTerminal(status: string): boolean {
  return ['PAID', 'FAILED', 'CANCELLED', 'EXPIRED'].includes(status);
}

/**
 * Determinar si un checkout fue exitoso.
 * El pago solo se considera exitoso cuando el backend confirma PAID.
 * NUNCA por regresar a success_url ni por asumir.
 */
export function isCheckoutPaid(status: string): boolean {
  return status === 'PAID';
}

// ═══════════════ CANCEL SUBSCRIPTION ═══════════════

/**
 * Solicitar cancelación de suscripción al backend PHP.
 * POST /api/v1/subscriptions/{subscriptionId}/cancel-request
 *
 * El frontend SOLO registra la solicitud.
 * El backend decide si cancela inmediatamente, al final del ciclo, o rechaza.
 */
export async function requestCancelSubscription(
  subscriptionId: string,
  reason?: string,
): Promise<CancelSubscriptionResponse> {
  const payload: CancelSubscriptionRequest = {
    subscription_id: subscriptionId,
  };

  if (reason) {
    payload.reason = reason;
  }

  try {
    const response = await backendApiRequest<CancelSubscriptionResponse>(
      `/api/v1/subscriptions/${encodeURIComponent(subscriptionId)}/cancel-request`,
      {
        method: 'POST',
        body: payload as unknown as Record<string, unknown>,
      },
    );
    return response.data;
  } catch (err: unknown) {
    if (err instanceof RfwBackendError) {
      throw mapCheckoutError(err.statusCode, err.message);
    }
    throw err;
  }
}

// ═══════════════ SESSION STORAGE ═══════════════

const CHECKOUT_ID_KEY = 'rfw_pending_checkout_id';
const CHECKOUT_STREAMING_MODE_KEY = 'rfw_pending_streaming_mode';

/**
 * Guardar checkout_id en sessionStorage antes de redirigir al procesador de pagos.
 * Se usa para recuperar el identificador cuando el usuario regresa a la success_url.
 */
export function storePendingCheckoutId(checkoutId: string, streamingMode?: string): void {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(CHECKOUT_ID_KEY, checkoutId);
      if (streamingMode) {
        sessionStorage.setItem(CHECKOUT_STREAMING_MODE_KEY, streamingMode);
      }
    } catch {
      // sessionStorage no disponible (ej. incognito con storage bloqueado)
    }
  }
}

/**
 * Recuperar y limpiar el checkout_id pendiente de sessionStorage.
 * Retorna null si no hay checkout pendiente.
 */
export function getAndClearPendingCheckoutId(): { checkoutId: string; streamingMode: string | null } | null {
  if (typeof window === 'undefined') return null;
  try {
    const checkoutId = sessionStorage.getItem(CHECKOUT_ID_KEY);
    const streamingMode = sessionStorage.getItem(CHECKOUT_STREAMING_MODE_KEY);
    sessionStorage.removeItem(CHECKOUT_ID_KEY);
    sessionStorage.removeItem(CHECKOUT_STREAMING_MODE_KEY);
    if (!checkoutId) return null;
    return { checkoutId, streamingMode: streamingMode || null };
  } catch {
    return null;
  }
}

// ═══════════════ REDIRECT ═══════════════

/**
 * Redirigir al usuario a la URL segura del checkout.
 * Guarda el checkout_id en sessionStorage antes de redirigir.
 * Solo redirige — no modifica ningún estado.
 */
export function redirectToCheckout(checkoutUrl: string, checkoutId?: string, streamingMode?: string): void {
  if (typeof window !== 'undefined') {
    if (checkoutId) {
      storePendingCheckoutId(checkoutId, streamingMode);
    }
    window.location.href = checkoutUrl;
  }
}

// ═══════════════ POLLING ═══════════════

/**
 * Hacer polling del estado de un checkout hasta que sea terminal.
 *
 * Solo lectura. Nunca modifica el estado.
 * El frontend consulta y reacciona visualmente.
 */
export async function pollCheckoutUntilTerminal(
  checkoutId: string,
  onStatusChange: (status: CheckoutStatusResponse) => void,
  intervalMs: number = 3000,
  maxAttempts: number = 120,
): Promise<CheckoutStatusResponse> {
  let attempts = 0;

  return new Promise<CheckoutStatusResponse>((resolve, reject) => {
    const poll = async () => {
      attempts += 1;

      try {
        const status = await getCheckoutStatus(checkoutId);
        onStatusChange(status);

        if (isCheckoutTerminal(status.status)) {
          resolve(status);
          return;
        }

        if (attempts >= maxAttempts) {
          reject(
            new Error('El checkout excedió el tiempo máximo de espera. Verificá tu suscripción manualmente.'),
          );
          return;
        }

        setTimeout(poll, intervalMs);
      } catch (err: unknown) {
        reject(err);
      }
    };

    poll();
  });
}

// ═══════════════ RE-EXPORT PARA CONVENIENCIA ═══════════════

export { checkPlanCompatibility, mapCheckoutError, buildCheckoutUrls };